import { z } from 'zod'
import type {HttpClient} from "@jantm/model-core";
import {createRelationApi} from "@/api/wiser/relation.ts";
import {defineModel} from '@jantm/model-core'
import RelationFullListItem from "@/templates/Wiser/RelationFullListItem.vue";
import RelationFullDetail from "@/templates/Wiser/RelationFullDetail.vue";
import {
  optionalBoolSchema,
  optionalDateTimeSchema, optionalNumberArraySchema,
  optionalNumberSchema,
  optionalStringSchema
} from "@/types/global.ts";

export const relationSchema = z.object({
  "id": z.int(),
  "title": optionalStringSchema,
  "teplannen": optionalStringSchema,
  "relatietype": optionalStringSchema,
  "bedrijfsnaam": optionalStringSchema,
  "loginAsUser": optionalStringSchema,
  "loginAsTestUser": optionalStringSchema,
  "vestiging": optionalStringSchema,
  "klantstatus": optionalStringSchema,
  "accountmanager": optionalStringSchema,
  "branche": optionalStringSchema,
  "aanhef": optionalStringSchema,
  "voorletters": optionalStringSchema,
  "voornaam": optionalStringSchema,
  "tussenvoegsel": optionalStringSchema,
  "achternaam": optionalStringSchema,
  "adres": optionalStringSchema,
  "huisnummer": optionalStringSchema,
  "toevoeging": optionalStringSchema,
  "postcode": optionalStringSchema,
  "plaats": optionalStringSchema,
  "land": optionalStringSchema,
  "geboortedatum": optionalStringSchema,
  "telefoonnr": optionalStringSchema,
  "gsmnr": optionalStringSchema,
  "email": optionalStringSchema,
  "betalingsconditie": optionalStringSchema,
  "geboorteplaats": optionalStringSchema,
  "emailfinancieel": optionalStringSchema,
  "bedrijfsgrootte": optionalStringSchema,
  "uitzonderingen": optionalStringSchema,
  "opsturenuitslag": optionalStringSchema,
  "opmerkingen": optionalStringSchema,
  "contactruimte": optionalStringSchema,
  "afspraken": optionalStringSchema,
  "blokkeren": optionalBoolSchema,
  "twinfield_id": optionalStringSchema,
  "credit_twinfieldid": optionalStringSchema,
  "kvk": optionalStringSchema,
  "btwnummer": optionalStringSchema,
  "relationnumber": optionalStringSchema,
  "hoofdcontactpersoon": optionalStringSchema,
  "stuursms": optionalBoolSchema,
  "aftersalesnieuweklant": optionalStringSchema,
  "printadreslabel": optionalStringSchema,
  "printadreslabel2": optionalStringSchema,
  "canlogin": optionalBoolSchema,
  "factuur_hoofdrelatie": optionalBoolSchema,
  "achteraf_factureren": optionalBoolSchema,
  "workbasefacturen": optionalNumberArraySchema,
  "financieel": optionalStringSchema,
  "prijsafspraakbijrelatie": optionalNumberArraySchema,
  "factuuronderrelatie": optionalNumberArraySchema,
  "relatieonderhoofdrelatie": optionalNumberArraySchema,
  "offerteonderrelatie": optionalNumberArraySchema,
  "adresonderrelatie": optionalNumberArraySchema,
  "contactonderrelatie": optionalNumberArraySchema,
  "cursistonderrelatie": optionalNumberArraySchema,
  "inverse-cursistonderrelatie": optionalNumberSchema,
  "inverse-cursistbijplanning": optionalNumberSchema,
  "inverse-cursistaanwezig": optionalNumberSchema,
  "inverse-relatieonderhoofdrelatie": optionalNumberSchema,
  "added_on": optionalDateTimeSchema,
  "changed_on": optionalDateTimeSchema
})

export type Relation = z.infer<typeof relationSchema>

export const relationCollectionSchema = z.array(relationSchema)

export type RelationCollection = z.infer<typeof relationCollectionSchema>

export const relationFilterSchema = z.object({
  fields: z.array(relationSchema.keyof()).nullish().optional(),
  page: optionalStringSchema.default('1'),
  perPage: optionalStringSchema.default('25'),
  filters: optionalStringSchema,
})
export type RelationFilter = z.infer<typeof relationFilterSchema>

export type RelationFilters = {
  id?: string
  relatietype?: string
  bedrijfsnaam?: string
  voornaam?: string
  achternaam?: string
}

export interface CreateRelationModelOptions {
  client: HttpClient
  /** Override when this model does not live at `/api/relations`. */
  endpoint?: string
}

export function createRelationModel(options: CreateRelationModelOptions) {
  return defineModel({
    name: 'Relation',
    schema: relationSchema,
    collectionSchema: relationCollectionSchema,
    filterSchema: relationFilterSchema,
    api: createRelationApi(options.client, options.endpoint),
    identify: (relation: Relation) => String(relation.id),
    templates: { FullListItem: RelationFullListItem, FullDetail: RelationFullDetail },
    // toItems: (collection) => Object.entries(collection.items).map(([, v]) => v),
    // toTotal: (collection => collection.metadata?.total)
  })
}

export type RelationModel = ReturnType<typeof createRelationModel>

declare module '@jantm/model-core' {
  interface ModelRegistry {
    Relation: RelationModel
  }
}
